import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../../../config';
import {
  apiToDraft,
  fetchFamilyInfo,
  postFamilyInfo,
  postFamilyInfoStepProgress,
  resolveSavedMemberDraft,
} from './familyInfoApi';
import {
  emptyMemberAddress,
  formatIfNotAddress,
  isMemberAddressComplete,
  parseIfNotAddress,
} from './familyAddress';
import { buildMemberSummary } from './familyInfoSummary';
import {
  deriveDependentIneligibility,
  deriveDependentTaxEligible,
  isNonSpouseTaxFlow,
  isUnder16AtTaxYearEnd,
  suggestPrimaryTaxCategoryKanji,
} from './familyInfoDependentEligibility';
import { deriveSpouseWithholding, isSpouseTaxFlow } from './familyInfoSpouseEligibility';
import { needsDisabilityFacts } from '../disabilityFacts';
import {
  clearNonSpouseTaxReentryFields,
  clearSpouseTaxReentryFields,
  finalizeStepDraft,
  firstMissingNonSpouseTaxStepFrom,
  firstMissingSpouseTaxStepFrom,
  hasHelpText,
  myNumberStepNeeded,
  nextStepAfter,
  prevStepForUndo,
  skipsEstimatedIncomeQuestion,
  stepHelpKey,
  stepQuestionKey,
  userAnswerForStep,
} from './familyInfoSteps';
import { normalizeMyNumber } from '../GeneralProfile/useGeneralProfileFlow';
import type { JpAddressValue } from '../components/JpAddressInput';
import {
  emptyMemberDraft,
  type CompletedMemberEntry,
  type FamilyMemberDraft,
  type FamilyStep,
  type ThreadMessage,
  type YesNo,
} from './familyInfoTypes';

function cloneDraft(d: FamilyMemberDraft): FamilyMemberDraft {
  return { ...d };
}

export type WheelDateValue = { year: number; month: number; day: number };

function defaultDobWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear() - 30, month: 1, day: 1 };
}

function defaultTaxChangeDateWheel(): WheelDateValue {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function syncWheelDateFromIso(iso: string, setWheel: (v: WheelDateValue) => void, emptyFallback: WheelDateValue) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    setWheel({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) });
    return;
  }
  setWheel(emptyFallback);
}

function syncDobFromDraft(dateOfBirth: string, setDob: (v: WheelDateValue) => void) {
  syncWheelDateFromIso(dateOfBirth, setDob, defaultDobWheel());
}

const KATAKANA_RE = /^[\u30A0-\u30FF\uFF66-\uFF9D\u3000\sー]+$/u;

/** Stagger intro then hasMembers question (aligned with general profile chat timing). */
const INTRO_REVEAL_MS = 150;
const HAS_MEMBERS_PROMPT_REVEAL_MS = 550;

export type UseFamilyInfoFlowArgs = {
  currentEmail?: string | null;
  invitationId?: string | null;
  onComplete: () => void;
  t: (key: string) => string;
  wheelLocale: string;
};

export function useFamilyInfoFlow({ currentEmail, invitationId, onComplete, t, wheelLocale }: UseFamilyInfoFlowArgs) {
  const taxYear = new Date().getFullYear();
  /** Opens on hasMembers so intro + Yes/No show without an extra Continue tap */
  const [step, setStep] = useState<FamilyStep>('hasMembers');
  const [introRevealed, setIntroRevealed] = useState(0);
  const [hasMembersPromptRevealed, setHasMembersPromptRevealed] = useState(0);
  const [introPinned, setIntroPinned] = useState(true);
  const [draft, setDraft] = useState<FamilyMemberDraft>(() => emptyMemberDraft(taxYear));
  const [completedMembers, setCompletedMembers] = useState<CompletedMemberEntry[]>([]);
  /** Summary index being edited inline (summary hidden, Q&A shown in its place). */
  const [expandedMemberIndex, setExpandedMemberIndex] = useState<number | null>(null);
  /** When set to finalConfirm, field saves return to expanded review instead of advancing the member flow. */
  const [editReturnStep, setEditReturnStep] = useState<FamilyStep | null>(null);
  /** Spouse tax gate no→yes during review edit: walk full tax branch before returning to summary. */
  const [editSpouseTaxReentry, setEditSpouseTaxReentry] = useState(false);
  /** Non-spouse tax gate no→yes during review edit: walk full tax branch before returning to summary. */
  const [editNonSpouseTaxReentry, setEditNonSpouseTaxReentry] = useState(false);
  const [activeMessages, setActiveMessages] = useState<ThreadMessage[]>([]);
  const [addAnother, setAddAnother] = useState<YesNo | ''>('');
  const [hasMembersAnswer, setHasMembersAnswer] = useState<YesNo | ''>('');
  const [hasMembersCommitted, setHasMembersCommitted] = useState(false);
  const [relationshipCommitted, setRelationshipCommitted] = useState(false);
  const [fullNameCommitted, setFullNameCommitted] = useState(false);
  const [fullNameKanaCommitted, setFullNameKanaCommitted] = useState(false);
  const [dobCommitted, setDobCommitted] = useState(false);
  const [sameHouseholdCommitted, setSameHouseholdCommitted] = useState(false);
  const [ifNotAddressCommitted, setIfNotAddressCommitted] = useState(false);
  const [outsideJapanCommitted, setOutsideJapanCommitted] = useState(false);
  const [taxGateCommitted, setTaxGateCommitted] = useState(false);
  const [sharedLivelihoodCommitted, setSharedLivelihoodCommitted] = useState(false);
  const [spouseFamilyWorkerCommitted, setSpouseFamilyWorkerCommitted] = useState(false);
  const [estimatedIncomeCommitted, setEstimatedIncomeCommitted] = useState(false);
  const [taxDependentCategoryCommitted, setTaxDependentCategoryCommitted] = useState(false);
  const [taxNonResidentCommitted, setTaxNonResidentCommitted] = useState(false);
  const [dependentIneligibleNoticeCommitted, setDependentIneligibleNoticeCommitted] = useState(false);
  const [disabilityStatusCommitted, setDisabilityStatusCommitted] = useState(false);
  const [disabilityFactsCommitted, setDisabilityFactsCommitted] = useState(false);
  const [changeDuringYearCommitted, setChangeDuringYearCommitted] = useState(false);
  const [nonResidentReasonCommitted, setNonResidentReasonCommitted] = useState(false);
  const [nonResidentReasonOtherCommitted, setNonResidentReasonOtherCommitted] = useState(false);
  const [remittanceAmountCommitted, setRemittanceAmountCommitted] = useState(false);
  const [taxChangeReasonCommitted, setTaxChangeReasonCommitted] = useState(false);
  const [taxChangeDateCommitted, setTaxChangeDateCommitted] = useState(false);
  const [insuranceRequiredCommitted, setInsuranceRequiredCommitted] = useState(false);
  const [myNumberLinkCommitted, setMyNumberLinkCommitted] = useState(false);
  const [supportedDependentCommitted, setSupportedDependentCommitted] = useState(false);
  const [myNumberCommitted, setMyNumberCommitted] = useState(false);
  const [addAnotherCommitted, setAddAnotherCommitted] = useState(false);
  /** Legacy aliases for removed residentJapan / taxCategories steps (keeps HMR-safe). */
  const residentJapanCommitted = taxDependentCategoryCommitted;
  const taxCategoriesCommitted = taxDependentCategoryCommitted;
  const withholdingSpouseCommitted = false;
  const [memberAddress, setMemberAddress] = useState<JpAddressValue>(emptyMemberAddress);
  const [dob, setDob] = useState(defaultDobWheel);
  const [taxChangeDateWheel, setTaxChangeDateWheel] = useState(defaultTaxChangeDateWheel);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const progressLoadedRef = useRef(false);
  /** Prevents late API hydration from resetting step after the user taps Continue */
  const userStepTouchedRef = useRef(false);

  const saveProgress = useCallback(
    async (nextStep: FamilyStep, status: 'in_progress' | 'final_confirm' | 'completed' = 'in_progress', memberDraft?: FamilyMemberDraft) => {
      if (!currentEmail || !API_BASE_URL) return;
      try {
        await postFamilyInfoStepProgress(currentEmail, nextStep, status, invitationId ?? undefined);
        try {
          await postFamilyInfo({
            email: currentEmail,
            invitationId: invitationId ?? undefined,
            familyInfoStatus: status,
            familyInfoStep: nextStep,
            draft: memberDraft ?? draft,
          });
        } catch (e) {
          console.warn('Family draft/member API save failed (step saved via general-profile)', e);
        }
        setSaveError(null);
      } catch {
        setSaveError(t('submitDocuments.familyInfo.saveFailed'));
      }
    },
    [currentEmail, invitationId, draft, t]
  );

  const resetMemberCommittedFlags = useCallback(() => {
    setRelationshipCommitted(false);
    setFullNameCommitted(false);
    setFullNameKanaCommitted(false);
    setDobCommitted(false);
    setSameHouseholdCommitted(false);
    setIfNotAddressCommitted(false);
    setOutsideJapanCommitted(false);
    setTaxGateCommitted(false);
    setSharedLivelihoodCommitted(false);
    setSpouseFamilyWorkerCommitted(false);
    setEstimatedIncomeCommitted(false);
    setTaxDependentCategoryCommitted(false);
    setTaxNonResidentCommitted(false);
    setDependentIneligibleNoticeCommitted(false);
    setDisabilityStatusCommitted(false);
    setChangeDuringYearCommitted(false);
    setNonResidentReasonCommitted(false);
    setNonResidentReasonOtherCommitted(false);
    setRemittanceAmountCommitted(false);
    setTaxChangeReasonCommitted(false);
    setTaxChangeDateCommitted(false);
    setInsuranceRequiredCommitted(false);
    setMyNumberLinkCommitted(false);
    setSupportedDependentCommitted(false);
    setMyNumberCommitted(false);
    setMemberAddress(emptyMemberAddress());
    setTaxChangeDateWheel(defaultTaxChangeDateWheel());
  }, []);

  const resetSpouseTaxCommittedFlags = useCallback(() => {
    setSharedLivelihoodCommitted(false);
    setSpouseFamilyWorkerCommitted(false);
    setEstimatedIncomeCommitted(false);
    setDisabilityStatusCommitted(false);
    setChangeDuringYearCommitted(false);
    setTaxChangeDateCommitted(false);
    setTaxChangeReasonCommitted(false);
  }, []);

  const resetNonSpouseTaxCommittedFlags = useCallback(() => {
    setSharedLivelihoodCommitted(false);
    setEstimatedIncomeCommitted(false);
    setTaxDependentCategoryCommitted(false);
    setTaxNonResidentCommitted(false);
    setDependentIneligibleNoticeCommitted(false);
    setNonResidentReasonCommitted(false);
    setNonResidentReasonOtherCommitted(false);
    setRemittanceAmountCommitted(false);
    setDisabilityStatusCommitted(false);
    setChangeDuringYearCommitted(false);
    setTaxChangeDateCommitted(false);
    setTaxChangeReasonCommitted(false);
  }, []);

  const applySnapshotToForm = useCallback(
    (snapshot: FamilyMemberDraft) => {
      setDraft(cloneDraft(snapshot));
      syncDobFromDraft(snapshot.dateOfBirth, setDob);
      setRelationshipCommitted(Boolean(snapshot.relationship));
      setFullNameCommitted(Boolean(snapshot.fullName.trim()));
      setFullNameKanaCommitted(Boolean(snapshot.fullNameKana.trim()));
      setDobCommitted(Boolean(snapshot.dateOfBirth));
      setSameHouseholdCommitted(snapshot.livingInSameHousehold === 'yes' || snapshot.livingInSameHousehold === 'no');
      setIfNotAddressCommitted(snapshot.livingInSameHousehold === 'no' && Boolean(snapshot.ifNotAddress.trim()));
      setOutsideJapanCommitted(
        snapshot.relationship === 'spouse' &&
          snapshot.livingInSameHousehold === 'no' &&
          (snapshot.outsideJapan === 'yes' || snapshot.outsideJapan === 'no')
      );
      setTaxGateCommitted(snapshot.claimTaxDependent === 'yes' || snapshot.claimTaxDependent === 'no');
      const taxYes = snapshot.claimTaxDependent === 'yes';
      const insuranceDone =
        snapshot.insuranceRequired === 'yes' ||
        snapshot.insuranceRequired === 'no' ||
        snapshot.registerHealthInsurance === 'yes' ||
        snapshot.registerHealthInsurance === 'no';
      setInsuranceRequiredCommitted(insuranceDone);
      if (insuranceDone && snapshot.insuranceRequired === 'yes') {
        setMyNumberLinkCommitted(Boolean(snapshot.myNumberLinkStatus));
        setSupportedDependentCommitted(
          snapshot.supportedDependent === 'yes' || snapshot.supportedDependent === 'no'
        );
      } else {
        setMyNumberLinkCommitted(false);
        setSupportedDependentCommitted(false);
      }
      setMyNumberCommitted(myNumberStepNeeded(snapshot) && Boolean(snapshot.myNumber.trim()));
      if (taxYes) {
        const spouseTax = snapshot.relationship === 'spouse';
        if (spouseTax) {
          setSharedLivelihoodCommitted(
            snapshot.sharedLivelihood === 'yes' || snapshot.sharedLivelihood === 'no'
          );
          setSpouseFamilyWorkerCommitted(
            snapshot.spouseFamilyWorker === 'yes' || snapshot.spouseFamilyWorker === 'no'
          );
          setEstimatedIncomeCommitted(
            Boolean(snapshot.estimatedIncome.trim()) &&
              !isUnder16AtTaxYearEnd(snapshot.dateOfBirth, snapshot.taxYear)
          );
          setDisabilityStatusCommitted(Boolean(snapshot.disabilityCategory));
          setDisabilityFactsCommitted(
            needsDisabilityFacts(snapshot.disabilityCategory) && Boolean(snapshot.disabilityFacts.trim())
          );
          setChangeDuringYearCommitted(
            snapshot.changeDuringYear === 'yes' ||
              snapshot.changeDuringYear === 'no' ||
              insuranceDone
          );
          setTaxChangeDateCommitted(
            (snapshot.changeDuringYear === 'yes' && Boolean(snapshot.taxChangeDate.trim())) ||
              insuranceDone
          );
          setTaxChangeReasonCommitted(
            (snapshot.changeDuringYear === 'yes' && Boolean(snapshot.taxChangeReason.trim())) ||
              insuranceDone
          );
          setTaxDependentCategoryCommitted(false);
          setTaxNonResidentCommitted(false);
          setDependentIneligibleNoticeCommitted(false);
          setNonResidentReasonCommitted(false);
          setNonResidentReasonOtherCommitted(false);
          setRemittanceAmountCommitted(false);
        } else {
          setSharedLivelihoodCommitted(
            snapshot.sharedLivelihood === 'yes' || snapshot.sharedLivelihood === 'no'
          );
          setSpouseFamilyWorkerCommitted(false);
          setEstimatedIncomeCommitted(
            Boolean(snapshot.estimatedIncome.trim()) &&
              !isUnder16AtTaxYearEnd(snapshot.dateOfBirth, snapshot.taxYear)
          );
          setTaxDependentCategoryCommitted(Boolean(snapshot.taxDependentCategory.trim()));
          setTaxNonResidentCommitted(
            snapshot.taxNonResident === 'yes' || snapshot.taxNonResident === 'no'
          );
          setDependentIneligibleNoticeCommitted(Boolean(snapshot.taxIneligibilityReason));
          setDisabilityStatusCommitted(Boolean(snapshot.disabilityCategory));
          setDisabilityFactsCommitted(
            needsDisabilityFacts(snapshot.disabilityCategory) && Boolean(snapshot.disabilityFacts.trim())
          );
          setChangeDuringYearCommitted(
            snapshot.changeDuringYear === 'yes' ||
              snapshot.changeDuringYear === 'no' ||
              insuranceDone
          );
          setNonResidentReasonCommitted(Boolean(snapshot.nonResidentReason));
          setNonResidentReasonOtherCommitted(
            Boolean(snapshot.nonResidentReason.trim()) &&
              snapshot.nonResidentReason !== 'none' &&
              !['study_abroad', 'disability', 'remittance_380k'].includes(snapshot.nonResidentReason)
          );
          setRemittanceAmountCommitted(Boolean(snapshot.remittanceAmount.trim()));
          setTaxChangeReasonCommitted(
            (snapshot.changeDuringYear === 'yes' && Boolean(snapshot.taxChangeReason.trim())) ||
              insuranceDone
          );
          setTaxChangeDateCommitted(
            (snapshot.changeDuringYear === 'yes' && Boolean(snapshot.taxChangeDate.trim())) ||
              insuranceDone
          );
        }
      } else {
        setSharedLivelihoodCommitted(false);
        setSpouseFamilyWorkerCommitted(false);
        setEstimatedIncomeCommitted(false);
        setTaxDependentCategoryCommitted(false);
        setTaxNonResidentCommitted(false);
        setDependentIneligibleNoticeCommitted(false);
        setDisabilityStatusCommitted(false);
        setDisabilityFactsCommitted(false);
        setChangeDuringYearCommitted(false);
        setNonResidentReasonCommitted(false);
        setNonResidentReasonOtherCommitted(false);
        setRemittanceAmountCommitted(false);
        setTaxChangeReasonCommitted(false);
        setTaxChangeDateCommitted(false);
      }
      setMemberAddress(parseIfNotAddress(snapshot.ifNotAddress));
      syncWheelDateFromIso(snapshot.taxChangeDate, setTaxChangeDateWheel, defaultTaxChangeDateWheel());
    },
    []
  );

  /** Upsert emp_family (address, non_resident_flag, etc.) before tax gate or on member complete. */
  const persistMemberToDb = useCallback(
    async (nextStep: FamilyStep, memberDraft?: FamilyMemberDraft) => {
      if (!currentEmail || !API_BASE_URL) return;
      const snapshot = cloneDraft(memberDraft ?? draft);
      if (!snapshot.relationship || !snapshot.fullName.trim()) return;
      try {
        const res = await postFamilyInfo({
          email: currentEmail,
          invitationId: invitationId ?? undefined,
          familyInfoStatus: 'in_progress',
          familyInfoStep: nextStep,
          saveMember: snapshot,
        });
        const saved = resolveSavedMemberDraft(res.members ?? [], snapshot, taxYear);
        if (saved?.id != null) {
          setDraft((d) => ({ ...d, id: saved.id }));
        }
      } catch (e) {
        console.warn('Family member DB save failed', e);
      }
    },
    [currentEmail, invitationId, draft, taxYear]
  );

  const finishCurrentMember = useCallback(async (memberDraft?: FamilyMemberDraft) => {
    if (!currentEmail) return false;
    const snapshot = cloneDraft(memberDraft ?? draft);
    try {
      await postFamilyInfoStepProgress(currentEmail, 'addAnother', 'in_progress', invitationId ?? undefined);
      const res = await postFamilyInfo({
        email: currentEmail,
        invitationId: invitationId ?? undefined,
        familyInfoStatus: 'in_progress',
        familyInfoStep: 'addAnother',
        saveMember: snapshot,
      });
      const saved = resolveSavedMemberDraft(res.members ?? [], snapshot, taxYear);
      if (saved) {
        Object.assign(snapshot, saved);
      }
      const summary = buildMemberSummary(snapshot, t);
      setCompletedMembers((prev) => {
        const entry = { text: summary, snapshot };
        if (expandedMemberIndex !== null && expandedMemberIndex < prev.length) {
          const next = [...prev];
          next[expandedMemberIndex] = entry;
          return next;
        }
        return [...prev, entry];
      });
      setExpandedMemberIndex(null);
      setActiveMessages([]);
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      setAddAnother('');
      setAddAnotherCommitted(false);
      setStep('addAnother');
      setSaveError(null);
      return true;
    } catch {
      setSaveError(t('submitDocuments.familyInfo.saveFailed'));
      return false;
    }
  }, [currentEmail, invitationId, draft, t, taxYear, resetMemberCommittedFlags, expandedMemberIndex]);

  const setCommittedForStep = useCallback((s: FamilyStep, committed: boolean) => {
    switch (s) {
      case 'relationship':
        setRelationshipCommitted(committed);
        break;
      case 'fullName':
        setFullNameCommitted(committed);
        break;
      case 'fullNameKana':
        setFullNameKanaCommitted(committed);
        break;
      case 'dob':
        setDobCommitted(committed);
        break;
      case 'sameHousehold':
        setSameHouseholdCommitted(committed);
        break;
      case 'ifNotAddress':
        setIfNotAddressCommitted(committed);
        break;
      case 'outsideJapan':
        setOutsideJapanCommitted(committed);
        break;
      case 'taxGate':
        setTaxGateCommitted(committed);
        break;
      case 'sharedLivelihood':
        setSharedLivelihoodCommitted(committed);
        break;
      case 'spouseFamilyWorker':
        setSpouseFamilyWorkerCommitted(committed);
        break;
      case 'estimatedIncome':
        setEstimatedIncomeCommitted(committed);
        break;
      case 'taxDependentCategory':
        setTaxDependentCategoryCommitted(committed);
        break;
      case 'taxNonResident':
        setTaxNonResidentCommitted(committed);
        break;
      case 'dependentIneligibleNotice':
        setDependentIneligibleNoticeCommitted(committed);
        break;
      case 'disabilityStatus':
        setDisabilityStatusCommitted(committed);
        break;
      case 'disabilityFacts':
        setDisabilityFactsCommitted(committed);
        break;
      case 'changeDuringYear':
        setChangeDuringYearCommitted(committed);
        break;
      case 'changeDate':
        setTaxChangeDateCommitted(committed);
        break;
      case 'changeReason':
      case 'changeReasonOther':
        setTaxChangeReasonCommitted(committed);
        break;
      case 'nonResidentReason':
        setNonResidentReasonCommitted(committed);
        break;
      case 'nonResidentReasonOther':
        setNonResidentReasonOtherCommitted(committed);
        break;
      case 'remittanceAmount':
        setRemittanceAmountCommitted(committed);
        break;
      case 'taxChangeReason':
        setTaxChangeReasonCommitted(committed);
        break;
      case 'taxChangeDate':
        setTaxChangeDateCommitted(committed);
        break;
      case 'insuranceRequired':
        setInsuranceRequiredCommitted(committed);
        break;
      case 'myNumberLink':
        setMyNumberLinkCommitted(committed);
        break;
      case 'supportedDependent':
        setSupportedDependentCommitted(committed);
        break;
      case 'myNumber':
        setMyNumberCommitted(committed);
        break;
      case 'hasMembers':
        setHasMembersCommitted(committed);
        break;
      case 'addAnother':
        setAddAnotherCommitted(committed);
        break;
      default:
        break;
    }
  }, []);

  const persistEditedMemberSnapshot = useCallback(
    async (index: number, snapshot: FamilyMemberDraft) => {
      if (!currentEmail || !API_BASE_URL) return;
      try {
        const res = await postFamilyInfo({
          email: currentEmail,
          invitationId: invitationId ?? undefined,
          familyInfoStatus: 'final_confirm',
          familyInfoStep: 'finalConfirm',
          saveMember: snapshot,
        });
        const saved = resolveSavedMemberDraft(res.members ?? [], snapshot, taxYear);
        if (saved) {
          const summary = buildMemberSummary(saved, t);
          setCompletedMembers((prev) => {
            const next = [...prev];
            if (index < 0 || index >= next.length) return prev;
            next[index] = { text: summary, snapshot: saved };
            return next;
          });
          applySnapshotToForm(saved);
          setSaveError(null);
        }
      } catch (e) {
        console.warn('Family member update during review failed', e);
        setSaveError(t('submitDocuments.familyInfo.saveFailed'));
      }
    },
    [currentEmail, invitationId, taxYear, t, applySnapshotToForm]
  );

  const maybeReturnAfterMemberEdit = useCallback(
    (memberDraft: FamilyMemberDraft): boolean => {
      if (editReturnStep !== 'finalConfirm' || expandedMemberIndex === null) return false;

      if (
        editSpouseTaxReentry &&
        isSpouseTaxFlow(memberDraft) &&
        memberDraft.claimTaxDependent === 'yes'
      ) {
        const missing = firstMissingSpouseTaxStepFrom('sharedLivelihood', memberDraft);
        if (missing !== 'insuranceRequired') {
          return false;
        }
        setEditSpouseTaxReentry(false);
      }

      if (
        editNonSpouseTaxReentry &&
        isNonSpouseTaxFlow(memberDraft) &&
        memberDraft.claimTaxDependent === 'yes'
      ) {
        const missing = firstMissingNonSpouseTaxStepFrom('sharedLivelihood', memberDraft);
        if (missing !== 'insuranceRequired' && missing !== 'dependentIneligibleNotice') {
          return false;
        }
        setEditNonSpouseTaxReentry(false);
      }

      const idx = expandedMemberIndex;
      const snapshot = cloneDraft(memberDraft);
      const summary = buildMemberSummary(snapshot, t);
      setCompletedMembers((prev) => {
        const next = [...prev];
        if (idx >= next.length) return prev;
        next[idx] = { text: summary, snapshot };
        return next;
      });
      applySnapshotToForm(snapshot);
      setStep('finalConfirm');
      void persistEditedMemberSnapshot(idx, snapshot);
      return true;
    },
    [
      editReturnStep,
      editSpouseTaxReentry,
      editNonSpouseTaxReentry,
      expandedMemberIndex,
      t,
      applySnapshotToForm,
      persistEditedMemberSnapshot,
    ]
  );

  const advanceFrom = useCallback(
    async (fromStep: FamilyStep, answerOverride?: string, memberDraft?: FamilyMemberDraft) => {
      userStepTouchedRef.current = true;
      const wasTaxNo = draft.claimTaxDependent === 'no';
      let working = finalizeStepDraft(fromStep, memberDraft ?? draft);
      const nowTaxYes = working.claimTaxDependent === 'yes';

      if (
        editReturnStep === 'finalConfirm' &&
        expandedMemberIndex !== null &&
        fromStep === 'taxGate' &&
        wasTaxNo &&
        nowTaxYes &&
        isSpouseTaxFlow(working)
      ) {
        working = clearSpouseTaxReentryFields(working);
        resetSpouseTaxCommittedFlags();
        setEditSpouseTaxReentry(true);
        setDraft(working);
        setTaxChangeDateWheel(defaultTaxChangeDateWheel());
        setCommittedForStep('taxGate', true);
        const first = firstMissingSpouseTaxStepFrom('sharedLivelihood', working);
        setStep(first);
        void saveProgress(first, 'in_progress', working);
        return;
      }

      if (
        editReturnStep === 'finalConfirm' &&
        expandedMemberIndex !== null &&
        fromStep === 'taxGate' &&
        wasTaxNo &&
        nowTaxYes &&
        isNonSpouseTaxFlow(working)
      ) {
        working = clearNonSpouseTaxReentryFields(working);
        resetNonSpouseTaxCommittedFlags();
        setEditNonSpouseTaxReentry(true);
        setDraft(working);
        setTaxChangeDateWheel(defaultTaxChangeDateWheel());
        setCommittedForStep('taxGate', true);
        const first = firstMissingNonSpouseTaxStepFrom('sharedLivelihood', working);
        setStep(first);
        void saveProgress(first, 'in_progress', working);
        return;
      }

      if (fromStep === 'taxGate' && working.claimTaxDependent === 'no') {
        setEditSpouseTaxReentry(false);
        setEditNonSpouseTaxReentry(false);
      }

      if (isSpouseTaxFlow(working)) {
        const derived = deriveSpouseWithholding(working);
        working = {
          ...working,
          withholdingSpouse: derived.withholdingSpouse,
          taxEligible: derived.taxEligible,
        };
      } else if (isNonSpouseTaxFlow(working)) {
        const reason = deriveDependentIneligibility(working, taxYear);
        const eligible = reason ? 'no' : deriveDependentTaxEligible(working, taxYear);
        working = {
          ...working,
          taxIneligibilityReason: reason,
          taxEligible: eligible || working.taxEligible,
          nonResidentFlag:
            working.taxNonResident === 'yes'
              ? true
              : working.taxNonResident === 'no'
                ? false
                : working.nonResidentFlag,
        };
      }
      if (memberDraft) {
        setDraft(working);
        if (fromStep === 'sameHousehold' && working.livingInSameHousehold === 'yes') {
          setMemberAddress(emptyMemberAddress());
        }
      }
      setCommittedForStep(fromStep, true);

      if (maybeReturnAfterMemberEdit(working)) return;

      const next = nextStepAfter(fromStep, working);
      if (next === 'addAnother') {
        const ok = await finishCurrentMember(working);
        if (!ok) return;
        return;
      }
      setStep(next);
      void saveProgress(next, 'in_progress', working);
    },
    [
      draft,
      editReturnStep,
      expandedMemberIndex,
      resetSpouseTaxCommittedFlags,
      resetNonSpouseTaxCommittedFlags,
      taxYear,
      saveProgress,
      finishCurrentMember,
      maybeReturnAfterMemberEdit,
      setCommittedForStep,
    ]
  );

  const canSendIfNotAddress = isMemberAddressComplete(memberAddress);

  const handleSendIfNotAddress = useCallback(async () => {
    if (!canSendIfNotAddress) return;
    userStepTouchedRef.current = true;
    const formatted = formatIfNotAddress(memberAddress);
    const updated: FamilyMemberDraft = { ...draft, ifNotAddress: formatted };
    setDraft(updated);
    setIfNotAddressCommitted(true);
    if (maybeReturnAfterMemberEdit(updated)) return;
    const next = nextStepAfter('ifNotAddress', updated);
    if (next === 'addAnother') {
      const ok = await finishCurrentMember(updated);
      if (!ok) return;
      return;
    }
    setStep(next);
    void saveProgress(next, 'in_progress', updated);
  }, [canSendIfNotAddress, memberAddress, draft, saveProgress, finishCurrentMember, maybeReturnAfterMemberEdit]);

  const handleOutsideJapan = useCallback(
    async (v: YesNo) => {
      userStepTouchedRef.current = true;
      const updated: FamilyMemberDraft = {
        ...draft,
        outsideJapan: v,
        nonResidentFlag: v === 'yes',
      };
      setDraft(updated);
      setOutsideJapanCommitted(true);
      if (maybeReturnAfterMemberEdit(updated)) return;
      await persistMemberToDb('taxGate', updated);
      setStep('taxGate');
      void saveProgress('taxGate', 'in_progress', updated);
    },
    [draft, persistMemberToDb, saveProgress, maybeReturnAfterMemberEdit]
  );

  const handleHasMembers = useCallback(
    async (v: YesNo) => {
      userStepTouchedRef.current = true;
      setHasMembersAnswer(v);
      setHasMembersCommitted(true);
      if (v === 'no') {
        setStep('finalConfirm');
        void saveProgress('finalConfirm', 'final_confirm');
        return;
      }
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      setStep('relationship');
      void saveProgress('relationship');
    },
    [saveProgress, taxYear, resetMemberCommittedFlags]
  );

  const handleAddAnother = useCallback(
    async (v: YesNo) => {
      userStepTouchedRef.current = true;
      setAddAnother(v);
      setAddAnotherCommitted(true);
      if (v === 'yes') {
        resetMemberCommittedFlags();
        setDraft(emptyMemberDraft(taxYear));
        setStep('relationship');
        void saveProgress('relationship');
        return;
      }
      setStep('finalConfirm');
      void saveProgress('finalConfirm', 'final_confirm');
    },
    [saveProgress, taxYear, resetMemberCommittedFlags]
  );

  const handleFinalComplete = useCallback(async () => {
    if (!currentEmail) {
      onComplete();
      return;
    }
    try {
      await postFamilyInfoStepProgress(currentEmail, 'finalConfirm', 'completed', invitationId ?? undefined);
      try {
        await postFamilyInfo({
          email: currentEmail,
          invitationId: invitationId ?? undefined,
          familyInfoStatus: 'completed',
          familyInfoStep: 'finalConfirm',
          completeSection: true,
        });
      } catch (e) {
        console.warn('Family complete API failed (marked complete via general-profile)', e);
      }
      onComplete();
    } catch {
      setSaveError(t('submitDocuments.familyInfo.saveFailed'));
    }
  }, [currentEmail, invitationId, onComplete, t]);

  const buildDobDraft = useCallback((): FamilyMemberDraft => {
    const { year, month, day } = dob;
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    let next: FamilyMemberDraft = { ...draft, dateOfBirth: iso };
    if (next.relationship !== 'spouse') {
      const suggested = suggestPrimaryTaxCategoryKanji(next, taxYear);
      if (suggested && !next.taxDependentCategory.trim()) {
        next = { ...next, taxDependentCategory: suggested };
      }
    }
    return finalizeStepDraft('dob', next);
  }, [dob, draft, taxYear]);

  const applyDobToDraft = useCallback(() => {
    const built = buildDobDraft();
    setDraft(built);
    return built.dateOfBirth;
  }, [buildDobDraft]);

  const buildTaxChangeDateDraft = useCallback((): FamilyMemberDraft => {
    const { year, month, day } = taxChangeDateWheel;
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return finalizeStepDraft('taxChangeDate', { ...draft, taxChangeDate: iso });
  }, [taxChangeDateWheel, draft]);

  const applyTaxChangeDateToDraft = useCallback(() => {
    const built = buildTaxChangeDateDraft();
    setDraft(built);
    return built.taxChangeDate;
  }, [buildTaxChangeDateDraft]);

  const skipTaxChangeDate = useCallback(() => {
    userStepTouchedRef.current = true;
    setTaxChangeDateWheel(defaultTaxChangeDateWheel());
    const updated = finalizeStepDraft('taxChangeDate', { ...draft, taxChangeDate: '' });
    setDraft(updated);
    void advanceFrom('taxChangeDate', t('submitDocuments.familyInfo.skipped'), updated);
  }, [advanceFrom, draft, t]);

  const validateKana = useCallback(
    (v: string) => {
      const s = v.trim();
      if (!s) return t('submitDocuments.familyInfo.required');
      if (!KATAKANA_RE.test(s)) return t('submitDocuments.familyInfo.kanaInvalid');
      return null;
    },
    [t]
  );

  const validateFullName = useCallback(
    (v: string) => {
      if (!v.trim()) return t('submitDocuments.familyInfo.required');
      return null;
    },
    [t]
  );

  const validateMyNumber = useCallback(
    (v: string) => {
      const n = normalizeMyNumber(v);
      if (!n) return t('submitDocuments.familyInfo.required');
      if (n.length !== 12) return t('submitDocuments.profileName.validation.myNumber');
      return null;
    },
    [t]
  );

  // Load progress once (do not overwrite step after user has continued)
  useEffect(() => {
    if (!currentEmail || !API_BASE_URL) return;
    if (progressLoadedRef.current) return;

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchFamilyInfo(currentEmail, invitationId ?? undefined);
        if (cancelled) return;
        progressLoadedRef.current = true;

        const members = data.members ?? [];
        setCompletedMembers(
          members.map((m) => {
            const snap = apiToDraft(m, taxYear);
            return { text: buildMemberSummary(snap, t), snapshot: snap };
          })
        );

        if (data.draft) {
          setDraft(apiToDraft(data.draft, taxYear));
        }

        if (!userStepTouchedRef.current) {
          const rawStep = data.familyInfoStep ?? 'hasMembers';
          const status = data.familyInfoStatus ?? '';
          let st: FamilyStep =
            rawStep === 'placeholder' || rawStep === 'intro' ? 'hasMembers' : (rawStep as FamilyStep);
          if (!st) st = 'hasMembers';
          if (data.familyInfoStatus === 'completed') {
            st = 'finalConfirm';
          }
          if (
            members.length > 0 &&
            (status === 'final_confirm' || status === 'completed' || st === 'finalConfirm')
          ) {
            st = 'finalConfirm';
          }
          setStep(st);
          setIntroPinned(true);
          if (st !== 'hasMembers') {
            setIntroRevealed(1);
            setHasMembersPromptRevealed(1);
          }
          if (members.length > 0) {
            setHasMembersAnswer('yes');
            setHasMembersCommitted(true);
            if (st === 'finalConfirm' || status === 'final_confirm' || status === 'completed') {
              setAddAnother('no');
              setAddAnotherCommitted(true);
            }
          } else if (st === 'finalConfirm' || status === 'final_confirm' || status === 'completed') {
            setHasMembersAnswer('no');
            setHasMembersCommitted(true);
          }
        }
      } catch {
        progressLoadedRef.current = true;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per email/session
  }, [currentEmail, invitationId]);

  /** After saving a member, never land back on hasMembers — use addAnother instead. */
  useEffect(() => {
    if (completedMembers.length > 0 && step === 'hasMembers' && hasMembersAnswer === 'yes') {
      setStep('addAnother');
    }
  }, [completedMembers.length, step, hasMembersAnswer]);

  useEffect(() => {
    if (step !== 'hasMembers') {
      setHasMembersPromptRevealed(1);
      return undefined;
    }
    setIntroRevealed(0);
    setHasMembersPromptRevealed(0);
    const tIntro = window.setTimeout(() => setIntroRevealed(1), INTRO_REVEAL_MS);
    const tHasMembers = window.setTimeout(() => setHasMembersPromptRevealed(1), HAS_MEMBERS_PROMPT_REVEAL_MS);
    return () => {
      window.clearTimeout(tIntro);
      window.clearTimeout(tHasMembers);
    };
  }, [step]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [
    introRevealed,
    hasMembersPromptRevealed,
    step,
    activeMessages,
    completedMembers,
    hasMembersCommitted,
    relationshipCommitted,
    fullNameCommitted,
    fullNameKanaCommitted,
    dobCommitted,
    sameHouseholdCommitted,
    ifNotAddressCommitted,
    outsideJapanCommitted,
    taxGateCommitted,
    sharedLivelihoodCommitted,
    spouseFamilyWorkerCommitted,
    estimatedIncomeCommitted,
    taxDependentCategoryCommitted,
    taxNonResidentCommitted,
    dependentIneligibleNoticeCommitted,
    disabilityStatusCommitted,
    disabilityFactsCommitted,
    changeDuringYearCommitted,
    nonResidentReasonCommitted,
    nonResidentReasonOtherCommitted,
    remittanceAmountCommitted,
    taxChangeReasonCommitted,
    taxChangeDateCommitted,
    insuranceRequiredCommitted,
    myNumberLinkCommitted,
    supportedDependentCommitted,
    myNumberCommitted,
    addAnotherCommitted,
    expandedMemberIndex,
  ]);

  const setDraftField = useCallback(<K extends keyof FamilyMemberDraft>(key: K, value: FamilyMemberDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const sendChoice = useCallback(
    (fromStep: FamilyStep, nextDraft: FamilyMemberDraft, answerText?: string) => {
      void advanceFrom(fromStep, answerText, nextDraft);
    },
    [advanceFrom]
  );

  const clearDraftForStep = useCallback(
    (s: FamilyStep) => {
      switch (s) {
        case 'relationship':
          setDraft((d) => ({ ...d, relationship: '' }));
          break;
        case 'fullName':
          setDraft((d) => ({ ...d, fullName: '' }));
          break;
        case 'fullNameKana':
          setDraft((d) => ({ ...d, fullNameKana: '' }));
          break;
        case 'dob':
          setDob(defaultDobWheel());
          setDraft((d) => ({ ...d, dateOfBirth: '' }));
          break;
        case 'sameHousehold':
          setDraft((d) => ({
            ...d,
            livingInSameHousehold: '',
            ifNotAddress: '',
            outsideJapan: '',
            nonResidentFlag: false,
            claimTaxDependent: '',
          }));
          setMemberAddress(emptyMemberAddress());
          break;
        case 'ifNotAddress':
          setMemberAddress(emptyMemberAddress());
          setDraft((d) => ({ ...d, ifNotAddress: '' }));
          break;
        case 'outsideJapan':
          setDraft((d) => ({ ...d, outsideJapan: '', nonResidentFlag: false }));
          break;
        case 'taxGate':
          setDraft((d) => ({
            ...d,
            claimTaxDependent: '',
            sharedLivelihood: '',
            spouseFamilyWorker: '',
            disabilityCategory: '',
            changeDuringYear: '',
            estimatedIncome: '',
            withholdingSpouse: '',
            taxDependentCategory: '',
            taxNonResident: '',
            taxIneligibilityReason: '',
            nonResidentReason: '',
            remittanceAmount: '',
            taxChangeReason: '',
            taxChangeDate: '',
          }));
          break;
        case 'sharedLivelihood':
          setDraft((d) => ({
            ...d,
            sharedLivelihood: '',
            taxIneligibilityReason: '',
            estimatedIncome: '',
          }));
          break;
        case 'taxDependentCategory':
          setDraft((d) => ({ ...d, taxDependentCategory: '' }));
          break;
        case 'taxNonResident':
          setDraft((d) => ({ ...d, taxNonResident: '', nonResidentReason: '', remittanceAmount: '' }));
          break;
        case 'dependentIneligibleNotice':
          setDraft((d) => ({ ...d, taxIneligibilityReason: '' }));
          break;
        case 'spouseFamilyWorker':
          setDraft((d) => ({ ...d, spouseFamilyWorker: '', estimatedIncome: '' }));
          break;
        case 'disabilityStatus':
          setDraft((d) => ({ ...d, disabilityCategory: '', disabilityFacts: '' }));
          break;
        case 'disabilityFacts':
          setDraft((d) => ({ ...d, disabilityFacts: '' }));
          break;
        case 'changeDuringYear':
          setDraft((d) => ({ ...d, changeDuringYear: '', taxChangeDate: '', taxChangeReason: '' }));
          setTaxChangeDateWheel(defaultTaxChangeDateWheel());
          break;
        case 'changeDate':
          setDraft((d) => ({ ...d, taxChangeDate: '' }));
          setTaxChangeDateWheel(defaultTaxChangeDateWheel());
          break;
        case 'changeReason':
        case 'changeReasonOther':
          setDraft((d) => ({ ...d, taxChangeReason: '' }));
          break;
        case 'nonResidentReasonOther':
          setDraft((d) => ({ ...d, nonResidentReason: '', taxIneligibilityReason: '' }));
          break;
        case 'estimatedIncome':
          setDraft((d) => ({ ...d, estimatedIncome: '', taxIneligibilityReason: '' }));
          break;
        case 'nonResidentReason':
          setDraft((d) => ({ ...d, nonResidentReason: '' }));
          break;
        case 'remittanceAmount':
          setDraft((d) => ({ ...d, remittanceAmount: '' }));
          break;
        case 'taxChangeReason':
          setDraft((d) => ({ ...d, taxChangeReason: '' }));
          break;
        case 'taxChangeDate':
          setDraft((d) => ({ ...d, taxChangeDate: '' }));
          setTaxChangeDateWheel(defaultTaxChangeDateWheel());
          break;
        case 'insuranceRequired':
          setDraft((d) => ({
            ...d,
            insuranceRequired: '',
            registerHealthInsurance: '',
            myNumberLinkStatus: '',
            supportedDependent: '',
            healthInsuranceDependent: '',
          }));
          break;
        case 'myNumberLink':
          setDraft((d) => ({ ...d, myNumberLinkStatus: '' }));
          break;
        case 'supportedDependent':
          setDraft((d) => ({ ...d, supportedDependent: '' }));
          break;
        case 'myNumber':
          setDraft((d) => ({ ...d, myNumber: '' }));
          break;
        case 'hasMembers':
          setHasMembersAnswer('');
          setHasMembersCommitted(false);
          break;
        case 'addAnother':
          setAddAnother('');
          setAddAnotherCommitted(false);
          break;
        default:
          break;
      }
    },
    []
  );

  const collapseExpandedMember = useCallback(
    (index: number) => {
      setExpandedMemberIndex(null);
      setCompletedMembers((prev) => prev.slice(0, index));
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      syncDobFromDraft('', setDob);
      setAddAnother('');
      setAddAnotherCommitted(false);
      if (index === 0) {
        setStep('hasMembers');
        void saveProgress('hasMembers');
      } else {
        setStep('addAnother');
        void saveProgress('addAnother');
      }
    },
    [resetMemberCommittedFlags, taxYear, saveProgress]
  );

  /** Expand a completed member summary for inline review/edit (final confirm or add-another). */
  const handleEditMemberSummary = useCallback(
    (index: number) => {
      userStepTouchedRef.current = true;
      const entry = completedMembers[index];
      if (!entry) return;
      setExpandedMemberIndex(index);
      applySnapshotToForm(entry.snapshot);
      setEditReturnStep('finalConfirm');
      setEditSpouseTaxReentry(false);
      setEditNonSpouseTaxReentry(false);
      setAddAnother((prev) => (prev === 'yes' || prev === 'no' ? prev : 'no'));
      setAddAnotherCommitted(true);
      setStep('finalConfirm');
      setSaveError(null);
    },
    [completedMembers, applySnapshotToForm]
  );

  const beginEditMemberField = useCallback(
    (fieldStep: FamilyStep) => {
      if (expandedMemberIndex === null) return;
      setEditReturnStep('finalConfirm');
      setStep(fieldStep);
    },
    [expandedMemberIndex]
  );

  const collapseMemberEditReview = useCallback(() => {
    setExpandedMemberIndex(null);
    setEditReturnStep(null);
    setEditSpouseTaxReentry(false);
    setEditNonSpouseTaxReentry(false);
    resetMemberCommittedFlags();
    setDraft(emptyMemberDraft(taxYear));
    syncDobFromDraft('', setDob);
    setStep('finalConfirm');
  }, [resetMemberCommittedFlags, taxYear]);

  /** Expand summary in place — prior summaries stay above; step back through fields with undo. */
  const handleUndoMemberSummary = useCallback(
    (index: number) => {
      userStepTouchedRef.current = true;
      const entry = completedMembers[index];
      if (!entry) return;
      setExpandedMemberIndex(index);
      applySnapshotToForm(entry.snapshot);
      setAddAnother('');
      setAddAnotherCommitted(false);
      const snap = entry.snapshot;
      const reopenStep: FamilyStep = (() => {
        if (myNumberStepNeeded(snap) && snap.myNumber.trim()) return 'myNumber';
        if (snap.insuranceRequired !== 'yes' && snap.insuranceRequired !== 'no') {
          return 'taxGate';
        }
        if (snap.insuranceRequired === 'yes') {
          if (snap.supportedDependent === 'yes' || snap.supportedDependent === 'no') {
            return 'supportedDependent';
          }
          if (snap.myNumberLinkStatus) return 'myNumberLink';
        }
        return 'insuranceRequired';
      })();
      if (reopenStep === 'taxGate') setTaxGateCommitted(false);
      if (reopenStep === 'insuranceRequired') setInsuranceRequiredCommitted(false);
      if (reopenStep === 'myNumberLink') {
        setInsuranceRequiredCommitted(true);
        setMyNumberLinkCommitted(false);
      }
      if (reopenStep === 'supportedDependent') {
        setInsuranceRequiredCommitted(true);
        setMyNumberLinkCommitted(true);
        setSupportedDependentCommitted(false);
      }
      if (reopenStep === 'myNumber') setMyNumberCommitted(false);
      setStep(reopenStep);
      void saveProgress(reopenStep, 'in_progress', snap);
    },
    [completedMembers, applySnapshotToForm, saveProgress]
  );

  const showEditOnSummary = useCallback(
    (index: number) => {
      if (expandedMemberIndex !== null) return false;
      if (index < 0 || index >= completedMembers.length) return false;
      return step === 'finalConfirm' || step === 'addAnother';
    },
    [step, completedMembers.length, expandedMemberIndex]
  );

  const showUndoOnSummary = useCallback(
    (index: number) => {
      if (showEditOnSummary(index)) return false;
      if (addAnotherCommitted) return false;
      if (expandedMemberIndex !== null) {
        return index === expandedMemberIndex - 1 && step === 'relationship';
      }
      if (index !== completedMembers.length - 1) return false;
      return (
        step === 'addAnother' ||
        step === 'finalConfirm' ||
        (step === 'hasMembers' && completedMembers.length > 0)
      );
    },
    [step, completedMembers.length, addAnotherCommitted, expandedMemberIndex, showEditOnSummary]
  );

  /** Undo on the summary above while expanding — collapse current member, expand the prior one. */
  const handleUndoSummaryDuringExpand = useCallback(
    (index: number) => {
      userStepTouchedRef.current = true;
      const expIdx = expandedMemberIndex;
      if (expIdx === null) {
        handleUndoMemberSummary(index);
        return;
      }
      if (index !== expIdx - 1) return;
      const priorEntry = completedMembers[index];
      if (!priorEntry) return;
      setExpandedMemberIndex(null);
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      syncDobFromDraft('', setDob);
      setAddAnother('');
      setAddAnotherCommitted(false);
      setExpandedMemberIndex(index);
      applySnapshotToForm(priorEntry.snapshot);
      setInsuranceRequiredCommitted(false);
      const priorSnap = priorEntry.snapshot;
      const reopenStep: FamilyStep = (() => {
        if (myNumberStepNeeded(priorSnap) && priorSnap.myNumber.trim()) return 'myNumber';
        if (priorSnap.insuranceRequired !== 'yes' && priorSnap.insuranceRequired !== 'no') {
          return 'taxGate';
        }
        if (priorSnap.insuranceRequired === 'yes') {
          if (priorSnap.supportedDependent === 'yes' || priorSnap.supportedDependent === 'no') {
            return 'supportedDependent';
          }
          if (priorSnap.myNumberLinkStatus) return 'myNumberLink';
        }
        return 'insuranceRequired';
      })();
      if (reopenStep === 'taxGate') setTaxGateCommitted(false);
      if (reopenStep === 'insuranceRequired') setInsuranceRequiredCommitted(false);
      if (reopenStep === 'myNumberLink') {
        setInsuranceRequiredCommitted(true);
        setMyNumberLinkCommitted(false);
      }
      if (reopenStep === 'supportedDependent') {
        setInsuranceRequiredCommitted(true);
        setMyNumberLinkCommitted(true);
        setSupportedDependentCommitted(false);
      }
      if (reopenStep === 'myNumber') {
        setInsuranceRequiredCommitted(
          priorSnap.insuranceRequired === 'yes' || priorSnap.insuranceRequired === 'no'
        );
        setMyNumberCommitted(false);
      }
      setStep(reopenStep);
      void saveProgress(reopenStep, 'in_progress', priorSnap);
    },
    [
      expandedMemberIndex,
      completedMembers,
      handleUndoMemberSummary,
      applySnapshotToForm,
      saveProgress,
      resetMemberCommittedFlags,
      taxYear,
    ]
  );

  /** Same pattern as general profile: uncommit previous answer and go back one step. */
  const handleUndo = useCallback(() => {
    userStepTouchedRef.current = true;

    if (step === 'finalConfirm') {
      if (addAnotherCommitted) {
        clearDraftForStep('addAnother');
        setAddAnotherCommitted(false);
        setStep('addAnother');
        void saveProgress('addAnother');
        return;
      }
      if (hasMembersCommitted) {
        clearDraftForStep('hasMembers');
        setHasMembersCommitted(false);
        setStep('hasMembers');
        void saveProgress('hasMembers');
        return;
      }
      return;
    }

    if (step === 'relationship' && relationshipCommitted && expandedMemberIndex !== null) {
      clearDraftForStep('relationship');
      setRelationshipCommitted(false);
      setStep('relationship');
      void saveProgress('relationship', 'in_progress', draft);
      return;
    }

    if (step === 'fullName' && relationshipCommitted) {
      clearDraftForStep('relationship');
      setRelationshipCommitted(false);
      setStep('relationship');
      void saveProgress('relationship', 'in_progress', draft);
      return;
    }
    if (step === 'fullNameKana' && fullNameCommitted) {
      clearDraftForStep('fullName');
      setFullNameCommitted(false);
      setStep('fullName');
      void saveProgress('fullName', 'in_progress', draft);
      return;
    }
    if (step === 'dob' && fullNameKanaCommitted) {
      clearDraftForStep('fullNameKana');
      setFullNameKanaCommitted(false);
      setStep('fullNameKana');
      void saveProgress('fullNameKana', 'in_progress', draft);
      return;
    }
    if (step === 'sameHousehold' && dobCommitted) {
      clearDraftForStep('dob');
      setDobCommitted(false);
      setStep('dob');
      void saveProgress('dob', 'in_progress', draft);
      return;
    }
    if (step === 'sameHousehold' && sameHouseholdCommitted) {
      clearDraftForStep('sameHousehold');
      setSameHouseholdCommitted(false);
      setIfNotAddressCommitted(false);
      setOutsideJapanCommitted(false);
      setTaxGateCommitted(false);
      setStep('sameHousehold');
      void saveProgress('sameHousehold', 'in_progress', draft);
      return;
    }
    if (step === 'ifNotAddress' && sameHouseholdCommitted) {
      clearDraftForStep('ifNotAddress');
      setIfNotAddressCommitted(false);
      setOutsideJapanCommitted(false);
      setTaxGateCommitted(false);
      setStep('ifNotAddress');
      void saveProgress('ifNotAddress', 'in_progress', draft);
      return;
    }
    if (step === 'outsideJapan' && ifNotAddressCommitted) {
      clearDraftForStep('outsideJapan');
      setOutsideJapanCommitted(false);
      setTaxGateCommitted(false);
      setStep('outsideJapan');
      void saveProgress('outsideJapan', 'in_progress', draft);
      return;
    }
    if (step === 'taxGate' && ifNotAddressCommitted && draft.relationship !== 'spouse') {
      clearDraftForStep('taxGate');
      setTaxGateCommitted(false);
      setStep('ifNotAddress');
      void saveProgress('ifNotAddress', 'in_progress', draft);
      return;
    }
    if (step === 'taxGate' && outsideJapanCommitted) {
      clearDraftForStep('outsideJapan');
      setOutsideJapanCommitted(false);
      setTaxGateCommitted(false);
      setStep('outsideJapan');
      void saveProgress('outsideJapan', 'in_progress', draft);
      return;
    }
    if (step === 'taxGate' && sameHouseholdCommitted && draft.livingInSameHousehold === 'yes') {
      clearDraftForStep('sameHousehold');
      setSameHouseholdCommitted(false);
      setTaxGateCommitted(false);
      setStep('sameHousehold');
      void saveProgress('sameHousehold', 'in_progress', draft);
      return;
    }
    const prevForUndo = prevStepForUndo(step, draft);
    const prevCommitted =
      prevForUndo === 'sharedLivelihood'
        ? sharedLivelihoodCommitted
        : prevForUndo === 'estimatedIncome'
          ? estimatedIncomeCommitted
          : prevForUndo === 'taxDependentCategory'
            ? taxDependentCategoryCommitted
            : prevForUndo === 'taxNonResident'
              ? taxNonResidentCommitted
              : prevForUndo === 'dependentIneligibleNotice'
                ? dependentIneligibleNoticeCommitted
                : prevForUndo === 'nonResidentReason'
                  ? nonResidentReasonCommitted
                  : prevForUndo === 'nonResidentReasonOther'
                    ? nonResidentReasonOtherCommitted
                    : prevForUndo === 'remittanceAmount'
                      ? remittanceAmountCommitted
                      : prevForUndo === 'changeDate'
                        ? taxChangeDateCommitted
                        : prevForUndo === 'changeReason' || prevForUndo === 'changeReasonOther'
                          ? taxChangeReasonCommitted
                          : prevForUndo === 'insuranceRequired'
                            ? insuranceRequiredCommitted
                            : prevForUndo === 'myNumberLink'
                              ? myNumberLinkCommitted
                              : prevForUndo === 'supportedDependent'
                                ? supportedDependentCommitted
                                : prevForUndo === 'myNumber'
                                  ? myNumberCommitted
                                  : prevForUndo === 'taxGate'
                                    ? taxGateCommitted
                                    : prevForUndo === 'outsideJapan'
                                      ? outsideJapanCommitted
                                      : prevForUndo === 'ifNotAddress'
                                        ? ifNotAddressCommitted
                                        : prevForUndo === 'sameHousehold'
                                          ? sameHouseholdCommitted
                                          : prevForUndo === 'dob'
                                            ? dobCommitted
                                            : prevForUndo === 'fullNameKana'
                                              ? fullNameKanaCommitted
                                              : prevForUndo === 'fullName'
                                                ? fullNameCommitted
                                                : prevForUndo === 'relationship'
                                                  ? relationshipCommitted
                                                  : prevForUndo === 'spouseFamilyWorker'
                                                    ? spouseFamilyWorkerCommitted
                                                    : prevForUndo === 'disabilityStatus'
                                                      ? disabilityStatusCommitted
                                                      : prevForUndo === 'disabilityFacts'
                                                        ? disabilityFactsCommitted
                                                      : prevForUndo === 'changeDuringYear'
                                                        ? changeDuringYearCommitted
                                                        : false;

    if (
      prevForUndo &&
      prevCommitted &&
      (step === 'estimatedIncome' ||
        step === 'taxDependentCategory' ||
        step === 'taxNonResident' ||
        step === 'dependentIneligibleNotice' ||
        step === 'nonResidentReason' ||
        step === 'nonResidentReasonOther' ||
        step === 'remittanceAmount' ||
        step === 'disabilityStatus' ||
        step === 'disabilityFacts' ||
        step === 'changeDuringYear' ||
        step === 'changeDate' ||
        step === 'changeReason' ||
        step === 'changeReasonOther' ||
        step === 'insuranceRequired' ||
        step === 'myNumberLink' ||
        step === 'supportedDependent' ||
        step === 'myNumber')
    ) {
      clearDraftForStep(prevForUndo);
      setCommittedForStep(prevForUndo, false);
      setStep(prevForUndo);
      void saveProgress(prevForUndo, 'in_progress', draft);
      return;
    }

    if (step === 'relationship' && addAnotherCommitted && expandedMemberIndex === null) {
      clearDraftForStep('addAnother');
      setAddAnotherCommitted(false);
      setAddAnother('');
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      syncDobFromDraft('', setDob);
      setStep('addAnother');
      void saveProgress('addAnother');
      return;
    }
    if (step === 'relationship' && hasMembersCommitted && expandedMemberIndex === null) {
      clearDraftForStep('hasMembers');
      setHasMembersCommitted(false);
      setHasMembersAnswer('');
      resetMemberCommittedFlags();
      setDraft(emptyMemberDraft(taxYear));
      syncDobFromDraft('', setDob);
      setStep('hasMembers');
      void saveProgress('hasMembers');
      return;
    }

    if (step === 'addAnother' && addAnotherCommitted) {
      clearDraftForStep('addAnother');
      setAddAnotherCommitted(false);
      setAddAnother('');
      void saveProgress('addAnother');
      return;
    }
  }, [
    step,
    draft,
    addAnotherCommitted,
    hasMembersCommitted,
    relationshipCommitted,
    fullNameCommitted,
    fullNameKanaCommitted,
    dobCommitted,
    sameHouseholdCommitted,
    ifNotAddressCommitted,
    outsideJapanCommitted,
    taxGateCommitted,
    sharedLivelihoodCommitted,
    spouseFamilyWorkerCommitted,
    estimatedIncomeCommitted,
    taxDependentCategoryCommitted,
    taxNonResidentCommitted,
    dependentIneligibleNoticeCommitted,
    disabilityStatusCommitted,
    disabilityFactsCommitted,
    changeDuringYearCommitted,
    nonResidentReasonCommitted,
    nonResidentReasonOtherCommitted,
    remittanceAmountCommitted,
    taxChangeReasonCommitted,
    taxChangeDateCommitted,
    insuranceRequiredCommitted,
    myNumberLinkCommitted,
    supportedDependentCommitted,
    myNumberCommitted,
    clearDraftForStep,
    saveProgress,
    resetMemberCommittedFlags,
    taxYear,
    expandedMemberIndex,
    collapseExpandedMember,
    setCommittedForStep,
  ]);

  const inProgressMemberVisible =
    expandedMemberIndex === null &&
    step !== 'finalConfirm' &&
    (relationshipCommitted ||
      fullNameCommitted ||
      fullNameKanaCommitted ||
      dobCommitted ||
      sameHouseholdCommitted ||
      ifNotAddressCommitted ||
      outsideJapanCommitted ||
      taxGateCommitted ||
      sharedLivelihoodCommitted ||
      spouseFamilyWorkerCommitted ||
      estimatedIncomeCommitted ||
      taxDependentCategoryCommitted ||
      taxNonResidentCommitted ||
      dependentIneligibleNoticeCommitted ||
      disabilityStatusCommitted ||
      disabilityFactsCommitted ||
      changeDuringYearCommitted ||
      nonResidentReasonCommitted ||
      nonResidentReasonOtherCommitted ||
      remittanceAmountCommitted ||
      taxChangeReasonCommitted ||
      taxChangeDateCommitted ||
      insuranceRequiredCommitted ||
      myNumberLinkCommitted ||
      supportedDependentCommitted ||
      myNumberCommitted);

  const showUndoHasMembers =
    step === 'relationship' && hasMembersCommitted && !addAnotherCommitted && expandedMemberIndex === null;
  const showUndoHasMembersDuringExpand =
    expandedMemberIndex === 0 && step === 'relationship' && hasMembersCommitted;
  const showUndoAddAnotherBeforeMember = step === 'relationship' && addAnotherCommitted;
  const showUndoAddAnotherAtAddAnother = step === 'addAnother' && addAnotherCommitted;
  const showUndoRelationship =
    (step === 'fullName' && relationshipCommitted) ||
    (expandedMemberIndex !== null && step === 'relationship' && relationshipCommitted);
  const showUndoFullName = step === 'fullNameKana' && fullNameCommitted;
  const showUndoFullNameKana = step === 'dob' && fullNameKanaCommitted;
  const showUndoDob = step === 'sameHousehold' && dobCommitted;
  const showUndoSameHousehold =
    (step === 'ifNotAddress' || step === 'taxGate') && sameHouseholdCommitted && draft.livingInSameHousehold === 'yes';
  const showUndoIfNotAddress =
    (step === 'outsideJapan' || (step === 'taxGate' && draft.relationship !== 'spouse')) &&
    ifNotAddressCommitted;
  const showUndoOutsideJapan = step === 'taxGate' && outsideJapanCommitted;
  const showUndoTaxGate =
    (step === 'sharedLivelihood' || step === 'estimatedIncome') && taxGateCommitted;
  const showUndoSharedLivelihood =
    (step === 'spouseFamilyWorker' ||
      step === 'estimatedIncome' ||
      step === 'dependentIneligibleNotice' ||
      (step === 'taxDependentCategory' && skipsEstimatedIncomeQuestion(draft) && !estimatedIncomeCommitted)) &&
    sharedLivelihoodCommitted;
  const showUndoSpouseFamilyWorker =
    (step === 'estimatedIncome' ||
      (step === 'disabilityStatus' &&
        isSpouseTaxFlow(draft) &&
        skipsEstimatedIncomeQuestion(draft) &&
        !estimatedIncomeCommitted)) &&
    spouseFamilyWorkerCommitted;
  const showUndoEstimatedIncome =
    (step === 'taxDependentCategory' || step === 'dependentIneligibleNotice' || step === 'disabilityStatus') &&
    estimatedIncomeCommitted;
  const showUndoTaxDependentCategory = step === 'taxNonResident' && taxDependentCategoryCommitted;
  const showUndoTaxNonResident =
    (step === 'nonResidentReason' || step === 'disabilityStatus') && taxNonResidentCommitted;
  const showUndoDependentIneligibleNotice = step === 'insuranceRequired' && dependentIneligibleNoticeCommitted;
  const showUndoDisabilityStatus =
    (step === 'disabilityFacts' || step === 'changeDuringYear') && disabilityStatusCommitted;
  const showUndoDisabilityFacts = step === 'changeDuringYear' && disabilityFactsCommitted;
  const showUndoChangeDuringYear =
    (step === 'changeDate' || step === 'insuranceRequired') && changeDuringYearCommitted;
  const showUndoChangeDate = (step === 'changeReason' || step === 'insuranceRequired') && taxChangeDateCommitted;
  const showUndoChangeReason =
    (step === 'changeReasonOther' || step === 'insuranceRequired') && taxChangeReasonCommitted;
  const showUndoChangeReasonOther = step === 'insuranceRequired' && taxChangeReasonCommitted;
  const showUndoNonResidentReason =
    (step === 'remittanceAmount' || step === 'nonResidentReasonOther' || step === 'disabilityStatus') &&
    nonResidentReasonCommitted;
  const showUndoNonResidentReasonOther =
    (step === 'disabilityStatus' || step === 'dependentIneligibleNotice') && nonResidentReasonOtherCommitted;
  const showUndoRemittanceAmount =
    (step === 'disabilityStatus' || step === 'disabilityFacts') && remittanceAmountCommitted;
  const showUndoInsuranceRequired =
    (step === 'myNumberLink' || step === 'supportedDependent' || (step === 'myNumber' && draft.insuranceRequired === 'no')) &&
    insuranceRequiredCommitted;
  const showUndoMyNumberLink = step === 'supportedDependent' && myNumberLinkCommitted;
  const showUndoSupportedDependent =
    (step === 'addAnother' || step === 'myNumber') && supportedDependentCommitted;
  const showUndoMyNumber = step === 'addAnother' && myNumberCommitted;
  const showUndoAddAnother = step === 'finalConfirm' && addAnotherCommitted;
  const showUndoHasMembersFinal = step === 'finalConfirm' && hasMembersCommitted && !addAnotherCommitted;
  const showUndoResidentJapan = showUndoTaxDependentCategory;
  const showUndoTaxCategories = showUndoTaxDependentCategory;
  const showUndoWithholdingSpouse = false;
  const showUndoTaxChangeReason = showUndoChangeReason;
  const showUndoTaxChangeDate = showUndoChangeDate;

  const helpTextForStep = useCallback(
    (s: FamilyStep) => {
      if (!hasHelpText(s)) return null;
      const key = stepHelpKey(s, draft);
      const text = t(key);
      return text === key ? null : text;
    },
    [t, draft]
  );

  return {
    step,
    introRevealed,
    hasMembersPromptRevealed,
    introPinned,
    draft,
    setDraft,
    setDraftField,
    completedMembers,
    expandedMemberIndex,
    inProgressMemberVisible,
    activeMessages,
    addAnother,
    hasMembersAnswer,
    dob,
    setDob,
    taxChangeDateWheel,
    setTaxChangeDateWheel,
    wheelLocale,
    taxYear,
    isLoading,
    saveError,
    threadEndRef,
    handleHasMembers,
    handleOutsideJapan,
    handleSendIfNotAddress,
    memberAddress,
    setMemberAddress,
    canSendIfNotAddress,
    handleAddAnother,
    handleFinalComplete,
    advanceFrom,
    applyDobToDraft,
    buildDobDraft,
    buildTaxChangeDateDraft,
    applyTaxChangeDateToDraft,
    skipTaxChangeDate,
    validateKana,
    validateFullName,
    validateMyNumber,
    sendChoice,
    saveProgress,
    handleUndo,
    hasMembersCommitted,
    relationshipCommitted,
    fullNameCommitted,
    fullNameKanaCommitted,
    dobCommitted,
    sameHouseholdCommitted,
    addAnotherCommitted,
    showUndoHasMembers,
    showUndoHasMembersDuringExpand,
    handleUndoSummaryDuringExpand,
    showUndoAddAnotherBeforeMember,
    showUndoAddAnotherAtAddAnother,
    showUndoRelationship,
    showUndoFullName,
    showUndoFullNameKana,
    showUndoDob,
    showUndoSameHousehold,
    showUndoIfNotAddress,
    showUndoOutsideJapan,
    showUndoTaxGate,
    ifNotAddressCommitted,
    outsideJapanCommitted,
    taxGateCommitted,
    sharedLivelihoodCommitted,
    spouseFamilyWorkerCommitted,
    residentJapanCommitted,
    estimatedIncomeCommitted,
    taxDependentCategoryCommitted,
    taxNonResidentCommitted,
    dependentIneligibleNoticeCommitted,
    disabilityStatusCommitted,
    disabilityFactsCommitted,
    changeDuringYearCommitted,
    withholdingSpouseCommitted,
    taxCategoriesCommitted,
    nonResidentReasonCommitted,
    nonResidentReasonOtherCommitted,
    remittanceAmountCommitted,
    taxChangeReasonCommitted,
    taxChangeDateCommitted,
    insuranceRequiredCommitted,
    myNumberLinkCommitted,
    supportedDependentCommitted,
    myNumberCommitted,
    showUndoSharedLivelihood,
    showUndoSpouseFamilyWorker,
    showUndoResidentJapan,
    showUndoEstimatedIncome,
    showUndoTaxDependentCategory,
    showUndoTaxNonResident,
    showUndoDependentIneligibleNotice,
    showUndoDisabilityStatus,
    showUndoDisabilityFacts,
    showUndoChangeDuringYear,
    showUndoChangeDate,
    showUndoChangeReason,
    showUndoChangeReasonOther,
    showUndoWithholdingSpouse,
    showUndoTaxCategories,
    showUndoNonResidentReason,
    showUndoNonResidentReasonOther,
    showUndoRemittanceAmount,
    showUndoTaxChangeReason,
    showUndoTaxChangeDate,
    showUndoInsuranceRequired,
    showUndoMyNumberLink,
    showUndoSupportedDependent,
    showUndoMyNumber,
    showUndoAddAnother,
    showUndoHasMembersFinal,
    handleEditMemberSummary,
    beginEditMemberField,
    collapseMemberEditReview,
    handleUndoMemberSummary,
    showEditOnSummary,
    showUndoOnSummary,
    editReturnStep,
    helpTextForStep,
    t,
  };
}
